/**
 * Utility functions for text processing and extraction
 */

/**
 * Extracts plain text from HTML content
 * @param html HTML string
 * @returns Plain text string
 */
export const extractTextFromHtml = (html: string): string => {
  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Extract text content
  const textContent = tempDiv.textContent || tempDiv.innerText || '';

  // Clean up extra whitespace
  return textContent.replace(/\s+/g, ' ').trim();
};

/**
 * Extracts plain text from rich text content with mention processing
 * Handles both HTML format and markdown-style mentions
 * @param content Rich text content (HTML or markdown)
 * @returns Plain text with mentions converted to @username format
 */
export const extractPlainTextWithMentions = (content: string): string => {
  let processedContent = content;

  // First, handle markdown-style mentions: @[DisplayName](userId) -> @DisplayName
  processedContent = processedContent.replace(/@\[([^\]]+)\]\(\d+\)/g, '@$1');

  // Then, handle HTML mentions: <span data-type="mention" data-id="123">DisplayName</span> -> @DisplayName
  processedContent = processedContent.replace(/<span[^>]*data-type="mention"[^>]*>([^<]+)<\/span>/g, '@$1');

  // Extract plain text from any remaining HTML
  return extractTextFromHtml(processedContent);
};

/**
 * Truncates text to a specified length with ellipsis
 * @param text Text to truncate
 * @param maxLength Maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * Converts RGB color string to hex format
 * @param rgb RGB color string in format "rgb(r, g, b)" or hex string
 * @returns Hex color string in format "#rrggbb"
 */
export const rgbToHex = (rgb: string): string => {
  if (!rgb || !rgb.startsWith('rgb(')) return rgb;
  const values = rgb.match(/\d+/g);
  if (!values || values.length !== 3) return rgb;
  const [r, g, b] = values.map((num) => parseInt(num).toString(16).padStart(2, '0'));
  return `#${r}${g}${b}`.toLowerCase();
};
